"use client";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaEllipsisV } from "react-icons/fa";
import { TfiAngleLeft } from "react-icons/tfi";
import ElementsIcon from "../../../images/icons/elements.svg";
import { VerticalTemplates } from "../../../services/Templates";
import CtaPreviewModal from "../pages/elemets/cta/preview/CtaPreviewModal";
import commonFolderService from "../../../services/commonFolderServices";

const CommonElementView = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [elements, setElements] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewElementData, setPreviewElementData] = useState(null);
  const [folderInfo, setFolderInfo] = useState(null);

  const navigate = useNavigate();
  const { folderId } = useParams();

  // Fetch common folder info
  useEffect(() => {
    const fetchFolderInfo = async () => {
      try {
        const folder = await commonFolderService.getCommonFolderById(folderId);
        setFolderInfo(folder);
      } catch (err) {
        console.error("Error fetching folder info:", err);
      }
    };

    if (folderId) {
      fetchFolderInfo();
    }
  }, [folderId]);

  // Fetch common elements by folder ID
  useEffect(() => {
    const fetchElements = async () => {
      if (!folderId) {
        setElements([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const elementsData = await commonFolderService.getCommonElementsByFolderId(folderId);
        // Handle both array response and object with elements property
        const elementsList = Array.isArray(elementsData) 
          ? elementsData 
          : (elementsData?.elements || []);
        setElements(elementsList);
      } catch (err) {
        console.error("Error fetching common elements:", err);
        setError(err.message || "Failed to load elements");
        setElements([]);
      } finally {
        setLoading(false);
      }
    };

    if (folderId) {
      fetchElements();
    }
  }, [folderId]);

  useEffect(() => {
    const handleClickOutside = () => {
      setShowMenu(null);
    };

    if (showMenu) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showMenu]);

  const getPreviewImage = (ele) => {
    if (ele.type === "cta") {
      const displayStyle = ele.displayStyle || "";
      const settings = ele.settings 
        ? (typeof ele.settings === "string" ? JSON.parse(ele.settings) : ele.settings)
        : {};
      const placement = settings.placement || "center";

      if (displayStyle.startsWith("vertical")) {
        return placement === "left"
          ? "/icons/BannerLeft.svg"
          : "/icons/BannerRight.svg";
      } else if (displayStyle.startsWith("horizontal")) {
        return placement === "top"
          ? "/icons/BannerTop.svg"
          : "/icons/BannerBottom.svg";
      } else if (displayStyle.startsWith("centered")) {
        return "/icons/BannerCentered.svg";
      }

      return (
        VerticalTemplates[ele.displayStyle]?.previewImage ||
        "/icons/BannerCentered.svg"
      );
    } else if (ele.type === "website") {
      return "/icons/Websites.svg";
    } else if (ele.type === "video") {
      return "/icons/Video.svg";
    } else if (ele.type === "document" || ele.type === "slide") {
      const settings = ele.settings 
        ? (typeof ele.settings === "string" ? JSON.parse(ele.settings) : ele.settings)
        : {};
      const filename = (settings?.filename || ele.name || "").toLowerCase();

      if (filename.includes(".pdf") || filename.includes("pdf")) {
        return "/icons/SlidesPDFDOC.svg";
      } else if (
        filename.includes(".ppt") ||
        filename.includes(".pptx") ||
        filename.includes("presentation")
      ) {
        return "/icons/Slides PPT Presentation.svg";
      } else {
        return "/icons/SlidesPDFDOC.svg";
      }
    } else if (ele.type === "prompter") {
      return "/icons/captions.svg";
    }

    return "/icons/BannerCentered.svg";
  };

  const previewElement = (element) => {
    if (element.type === "cta") {
      setPreviewElementData(element);
      setShowPreviewModal(true);
    } else if (element.type === "website") {
      const settings = element.settings 
        ? (typeof element.settings === "string" ? JSON.parse(element.settings) : element.settings)
        : {};
      if (settings.link) {
        let url = settings.link;
        // Check if link already has a protocol, if not add https://
        if (!url.match(/^https?:\/\//i)) {
          url = `https://${url}`;
        }
        window.open(url, "_blank");
      }
    } else if (element.type === "prompter") {
      setPreviewElementData(element);
      setShowPreviewModal(true);
    }
  };

  const exportElement = (element) => {
    if (!element) return;

    // Clean element by removing _id and other internal fields
    const { _id, id, userId, createdBy, commonFolderId, ...exportData } = element;

    // Ensure settings is a string (not an object) to match backend expectations
    if (exportData.settings !== undefined && exportData.settings !== null) {
      if (typeof exportData.settings === "object") {
        exportData.settings = JSON.stringify(exportData.settings);
      } else if (typeof exportData.settings === "string") {
        try {
          const parsed = JSON.parse(exportData.settings);
          exportData.settings = JSON.stringify(parsed);
        } catch (e) {
          exportData.settings = JSON.stringify(exportData.settings);
        }
      }
    }

    // Convert object to formatted JSON
    const jsonData = JSON.stringify(exportData, null, 2);

    // Create a blob
    const blob = new Blob([jsonData], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    // Create a temporary link
    const link = document.createElement("a");
    link.href = url;
    link.download = `${element.name || "element"}-export.rlk`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleBack = () => {
    navigate("/dashboard/folders");
  };

  return (
    <div className="h-screen mt-10 md:mt-0">
      <div className="w-full max-w-6xl mx-auto py-3 md:py-6 px-0 primary-card-background rounded-xl md:rounded-2xl h-[calc(100vh-100px)]">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 md:mb-6 px-3 md:px-6">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <button
              onClick={handleBack}
              className="text-white hover:text-gray-300 transition-colors p-1 flex-shrink-0"
            >
              <TfiAngleLeft className="w-4 h-4 md:w-5 md:h-5" />
            </button>
            <span className="text-white text-base md:text-lg font-semibold whitespace-nowrap">
              COMMON ELEMENTS
            </span>
            <TfiAngleLeft className="text-white/80 text-sm md:text-base flex-shrink-0" />
            <span className="text-white/80 text-xs md:text-sm truncate">
              {folderInfo?.name || ""}
            </span>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-3 md:mb-4 p-3 md:p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm md:text-base">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-2 text-red-400 hover:text-red-300"
            >
              ×
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-6 md:py-8">
            <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-white mx-auto"></div>
            <p className="mt-2 text-gray-400 text-sm md:text-base">
              Loading elements...
            </p>
          </div>
        )}

        {showPreviewModal && (
          <CtaPreviewModal
            element={previewElementData}
            onClose={() => setShowPreviewModal(false)}
          />
        )}

        {/* Elements Section */}
        <div className="bg-black rounded-xl md:rounded-2xl py-3 md:py-4 sm:py-6 md:py-8 px-0 min-h-[300px] md:min-h-[400px]">
          <div className="flex flex-col sm:flex-row justify-between mb-4 gap-3 sm:gap-4 px-3 md:px-4 sm:px-6 md:px-8">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-6 h-6 md:w-7 md:h-7 sm:w-8 sm:h-8 border-2 border-white rounded flex items-center justify-center flex-shrink-0">
                <img
                  className="h-4 w-3 md:h-5 md:w-3 sm:h-6 sm:w-4"
                  alt="Folders"
                  src={ElementsIcon}
                />
              </div>
              <span className="text-white text-sm md:text-base sm:text-lg md:text-xl font-medium">
                ELEMENTS
              </span>
            </div>
          </div>

          <div
            className={`${
              elements.length === 0
                ? "flex flex-col justify-center items-center"
                : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-1 md:gap-1.5"
            } min-h-[200px] md:min-h-[300px] px-3 md:px-4 sm:px-6 md:px-8`}
          >
            {elements.length === 0 && !loading && (
              <div className="text-center text-gray-400">
                <p className="text-base md:text-lg">No elements found in this common folder</p>
              </div>
            )}

            {elements.length > 0 &&
              elements.map((element) => (
                <div
                  key={element._id}
                  className={`relative group cursor-pointer p-0.5 md:p-0.5 rounded-lg transition-all duration-200 bg-[#ffffff1f] flex flex-col hover:bg-[#ffffff2f] border border-transparent hover:border-gray-600 min-h-[90px] md:min-h-[120px] mx-0 my-0.5 md:my-2`}
                  onClick={() => {
                    previewElement(element);
                  }}
                >
                  {/* File Thumbnail */}
                  <div className="aspect-square mb-0.5 md:mb-2 rounded-lg overflow-hidden bg-gray-700 flex items-center justify-center w-full p-6 md:p-6">
                    <img
                      src={getPreviewImage(element)}
                      alt={element.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = "flex";
                        }
                      }}
                    />
                  </div>

                  {/* File Name */}
                  <p className="text-sm sm:text-base md:text-[14px] text-white truncate text-center mt-auto mb-1 md:mb-2 px-1">
                    {element.name}
                  </p>

                  {/* 3-dot Menu Button - Only Export */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(element._id);
                    }}
                    className="absolute top-1 md:top-2 right-1 md:right-2 p-1 bg-gray-700/90 hover:bg-gray-600/90 rounded opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-20"
                  >
                    <FaEllipsisV className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" />
                  </button>

                  {/* Menu Dropdown - Only Export Option */}
                  {showMenu === element._id && (
                    <div
                      className="absolute top-8 sm:top-10 right-0 w-32 sm:w-30 md:w-24 bg-[#2A2835] border border-gray-600 rounded-md shadow-lg z-50"
                      style={{
                        boxShadow:
                          "0 10px 25px rgba(0, 0, 0, 0.5), 0 0 10px rgba(0, 0, 0, 0.3)",
                      }}
                    >
                      <div className="py-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setShowMenu(null);
                            exportElement(element);
                          }}
                          className="block w-full text-left px-4 sm:px-4 md:px-3 py-3 sm:py-2 md:py-1.5 text-sm sm:text-sm md:text-xs text-white hover:bg-gray-700 hover:text-white-400 active:bg-gray-600"
                        >
                          Export
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonElementView;
